/**
 * components/admin/CourierApiIntegrationCard.jsx · v2.7.58+E37 · Section 5.2
 * ═══════════════════════════════════════════════════════════════════════
 * FUTURE COURIER API INTEGRATION POINT · placeholder configuration surface
 * per DELIVERY_SYSTEM.md §5.2. This card stores admin-entered credentials
 * and webhook targets for a downstream external courier partner (Uber
 * Direct, DoorDash Drive, Roadie, Onfleet, Bringg, …). NO outbound calls
 * fire from this schema today — it's a wired-but-quiet extensibility
 * point so the operator can slot in a real provider without disrupting
 * the core delivery models.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Plug, Save, Loader2, ShieldAlert, Radio, RefreshCw,
} from "lucide-react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";

const FIELDS = [
  { key: "provider_name",            label: "Provider Name",           placeholder: "uber_direct · doordash_drive · roadie · onfleet · …", testid: "cfg-provider" },
  { key: "api_base_url",             label: "API Base URL",             placeholder: "https://api.provider.com/v1",                          testid: "cfg-api-url" },
  { key: "api_key",                  label: "API Key",                  placeholder: "•••••••• (write-only)", type: "password",                testid: "cfg-api-key" },
  { key: "webhook_signing_secret",   label: "Webhook Signing Secret",   placeholder: "hex-encoded HMAC secret", type: "password",              testid: "cfg-webhook-secret" },
  { key: "completion_webhook_url",   label: "Completion Webhook URL",   placeholder: "https://provider.com/webhooks/delivery-completed",     testid: "cfg-webhook-url" },
  { key: "status_tracking_template", label: "Status Tracking URL",      placeholder: "https://track.provider.com/{tracking_id}",              testid: "cfg-tracking" },
];

export default function CourierApiIntegrationCard() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await adminApi.get("/deliveries/admin/courier-config");
      setCfg(r.data);
      setForm(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load courier config");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changed = cfg && (
    FIELDS.some((f) => (form[f.key] ?? "") !== (cfg[f.key] ?? ""))
    || (form.enabled !== cfg.enabled)
    || ((form.notes ?? "") !== (cfg.notes ?? ""))
  );

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        enabled: !!form.enabled,
        notes:   form.notes || "",
      };
      for (const f of FIELDS) payload[f.key] = form[f.key] || "";
      const r = await adminApi.put("/deliveries/admin/courier-config", payload);
      setCfg(r.data);
      setForm(r.data);
      toast.success("Courier integration config saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!cfg) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 p-6" data-testid="courier-config-loading">
        <div className="text-center text-zinc-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin inline mr-1"/>Loading integration config…
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900 border-2 border-dashed border-zinc-700 p-4 space-y-4" data-testid="courier-integration-card">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-sky-500/15 border border-sky-400/40 flex items-center justify-center flex-shrink-0">
          <Plug className="h-6 w-6 text-sky-300"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-base font-bold text-zinc-100">FUTURE COURIER API INTEGRATION POINT</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            Placeholder schema per <code className="text-sky-300">DELIVERY_SYSTEM.md §5.2</code>. Stores credentials and webhook targets so a downstream sprint can wire external couriers (Uber Direct, DoorDash Drive, Roadie, Onfleet, Bringg, …) without touching the core delivery models.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={!!form.enabled}
            onCheckedChange={(v) => setForm((s) => ({ ...s, enabled: v }))}
            data-testid="courier-enabled-toggle"
          />
          <span className={`text-[10px] font-extrabold uppercase tracking-widest ${form.enabled ? "text-emerald-300" : "text-zinc-500"}`}>
            {form.enabled ? "Enabled" : "Disabled"}
          </span>
        </div>
      </div>

      <div className="bg-amber-500/5 border border-amber-400/30 rounded p-3 text-[11px] text-amber-200 flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-300"/>
        <div>
          <span className="font-bold">Placeholder — no outbound calls fire.</span> Saving this config
          only persists the values. The completion webhook / tracking API is invoked from the
          <code className="text-amber-100 font-bold"> driver_mark_delivered() </code>
          fan-out block once a downstream sprint activates the hook.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="courier-config-fields">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{f.label}</Label>
            <Input
              type={f.type || "text"}
              value={form[f.key] || ""}
              onChange={set(f.key)}
              placeholder={f.placeholder}
              className="bg-zinc-950 border-zinc-700 mt-1 text-sm"
              data-testid={f.testid}
            />
          </div>
        ))}
      </div>

      <div>
        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Integration Notes</Label>
        <Textarea
          value={form.notes || ""}
          onChange={set("notes")}
          rows={3}
          placeholder="Free-text notes for the ops team — provisioning status, contact person, contract dates, etc."
          className="bg-zinc-950 border-zinc-700 mt-1 text-xs"
          data-testid="cfg-notes"
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] text-zinc-500 flex items-center gap-1">
          {cfg.updated_at
            ? <><Radio className="h-3 w-3 text-emerald-300"/> Last updated {new Date(cfg.updated_at).toLocaleString()} by {cfg.updated_by || "—"}</>
            : <>Never saved — schema seeded with defaults.</>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} size="sm" className="bg-zinc-950 border-zinc-700 text-xs" data-testid="cfg-reload">
            <RefreshCw className="h-3.5 w-3.5 mr-1"/>Reload
          </Button>
          <Button
            onClick={save}
            disabled={!changed || busy}
            className="bg-sky-400 hover:bg-sky-300 text-zinc-950 font-extrabold h-9"
            data-testid="cfg-save-courier"
          >
            {busy
              ? <><Loader2 className="h-4 w-4 animate-spin mr-1"/>Saving…</>
              : <><Save className="h-4 w-4 mr-1"/>{changed ? "Save Config" : "No Changes"}</>}
          </Button>
        </div>
      </div>
    </Card>
  );
}
